/**
 * Sensitive Data Redaction Utilities
 *
 * Automatically redacts sensitive information from log output
 * to prevent accidental exposure of credentials, tokens, and PII.
 */
/**
 * Additional redaction patterns. Default sensitive key handling is segment-aware
 * through isSensitiveKey(), so fields such as "author", "audit", and
 * "entityKey" are not redacted as accidental substring matches.
 */
export const DEFAULT_REDACT_PATTERNS = [];
/**
 * Redaction placeholder
 */
export const REDACTED = "[REDACTED]";
const SENSITIVE_KEY_SEGMENTS = new Set([
    "password",
    "secret",
    "token",
    "auth",
    "authorization",
    "credential",
    "credentials",
    "private",
    "bearer",
    "jwt",
    "cookie",
    "ssn",
    "cvv",
    "email",
]);
function keySegments(key) {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .split(/[\s._-]+/)
        .filter(Boolean);
}
/**
 * Match sensitive names as whole key segments ("authToken", "api_key",
 * "Authorization"), never as arbitrary substrings.
 */
export function isSensitiveKey(key) {
    const segments = keySegments(key);
    return segments.some((segment, index) => {
        if (SENSITIVE_KEY_SEGMENTS.has(segment))
            return true;
        const previous = segments[index - 1];
        const next = segments[index + 1];
        return ((segment === "key" && previous === "api") ||
            (segment === "api" && next === "key") ||
            (segment === "key" && previous === "private") ||
            (segment === "card" && previous === "credit") ||
            (segment === "credit" && next === "card") ||
            (segment === "number" && previous === "card"));
    });
}
/**
 * Check if a key matches any redaction pattern
 */
export function shouldRedactKey(key, patterns = DEFAULT_REDACT_PATTERNS) {
    return isSensitiveKey(key) || patterns.some((pattern) => pattern.test(key));
}
/**
 * Check if a string value looks like a sensitive token
 * (long alphanumeric strings that might be API keys, JWTs, etc.)
 */
export function looksLikeSensitiveValue(value) {
    // JWT pattern (three base64 segments separated by dots)
    if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) {
        return true;
    }
    // Long alphanumeric strings (likely API keys/tokens)
    // At least 32 chars of mostly alphanumeric
    if (value.length >= 32 && /^[A-Za-z0-9_-]{32,}$/.test(value)) {
        return true;
    }
    // Bearer token pattern
    if (/^Bearer\s+/i.test(value)) {
        return true;
    }
    return false;
}
/**
 * Redact sensitive values from an object (shallow)
 */
export function redactObject(obj, patterns = DEFAULT_REDACT_PATTERNS) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        result[key] = redactValue(key, value, patterns);
    }
    return result;
}
/**
 * Redact a single value based on its key or content
 */
export function redactValue(key, value, patterns = DEFAULT_REDACT_PATTERNS) {
    // Redact based on key name
    if (shouldRedactKey(key, patterns)) {
        return REDACTED;
    }
    // Redact string values that look sensitive
    if (typeof value === "string" && looksLikeSensitiveValue(value)) {
        return REDACTED;
    }
    // Recursively handle nested objects
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        return redactObject(value, patterns);
    }
    // Handle arrays
    if (Array.isArray(value)) {
        return value.map((item, index) => redactValue(String(index), item, patterns));
    }
    return value;
}
/**
 * Create a custom redaction function with additional patterns
 */
export function createRedactor(additionalPatterns = []) {
    const allPatterns = [...DEFAULT_REDACT_PATTERNS, ...additionalPatterns];
    return (key, value) => redactValue(key, value, allPatterns);
}
/**
 * Hash a value for logging (useful for user IDs that need correlation but privacy)
 * Uses a simple hash - NOT cryptographically secure, just for log correlation
 */
export function hashForLogging(value) {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        const char = value.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Return positive hex string with prefix for identification
    return `hash_${Math.abs(hash).toString(16)}`;
}
